import { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { VideoFrameShaderReadAccess, getVideoFrameShaderAccess } from '@mimir/browser/gldraw/videoFrameShaderReadAccess';
import { WebGlWrapper } from '@mimir/browser/gldraw/webGlWrapper';
import { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';

export class RawFrameRenderer {
  private renderThread : Promise<void> | null = null;
  private pendingFrame : VideoFrameV2 | null = null;

  constructor(
    private webGlWrapper: WebGlWrapper,
    private bufferCache: BufferCache,
  ) { }

  renderFrame(videoFrame: VideoFrameV2): void {
    if (this.pendingFrame !== null) {
      console.warn('Dropping frame in renderer');
      this.pendingFrame.buffer.close();
      this.pendingFrame = null;
    }
    this.pendingFrame = videoFrame;
    if (this.renderThread === null) {
      this.renderThread =
        this.renderFrameInternalLoop()
          .catch((error) => console.error(`Error on render thread: ${error}`))
          .finally(() => this.renderThread = null);
    }
  }

  async renderFrameInternalLoop() {
    while (this.pendingFrame !== null) {
      const videoFrame = this.pendingFrame;
      this.pendingFrame = null;
      this.renderFrameInternal(videoFrame);
    }
  }

  async renderFrameInternal(videoFrame: VideoFrameV2) {
    const gl = this.webGlWrapper.getGlContext();

    this.webGlWrapper.throwIfError();

    const shaderReadAccess = getVideoFrameShaderAccess(this.webGlWrapper, videoFrame, this.bufferCache, false);

    this.webGlWrapper.throwIfError();

    const fragmentShaderSource = buildSimpleRenderFragmentShaderSource(shaderReadAccess);

    this.webGlWrapper.throwIfError();

    const program =
      this.webGlWrapper.getLinkedProgram(
        VERTEX_SHADER_SOURCE_COMMON,
        fragmentShaderSource,
      );

    this.webGlWrapper.throwIfError();

    this.webGlWrapper.throwIfError();

    gl.useProgram(program);

    this.webGlWrapper.throwIfError();

    let textureSlot = 0;
    shaderReadAccess.bind(gl, program, textureSlot);

    this.webGlWrapper.throwIfError();

    const canvas = this.webGlWrapper.canvas;

    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    shaderReadAccess.close();

    this.webGlWrapper.throwIfError();

    videoFrame.buffer.close();
  }
}

function buildSimpleRenderFragmentShaderSource(shaderReadAccess: VideoFrameShaderReadAccess): string {
  return `\
    #version 300 es
    precision mediump float;

    in vec2 v_uv;

    out vec4 o_color;

    ${shaderReadAccess.readFunctionSource}

    void main() {
      o_color = ${shaderReadAccess.readFunctionName}(v_uv);
    }
  `;
}

const VERTEX_SHADER_SOURCE_COMMON : string = `\
#version 300 es

out vec2 v_uv;

void main() {
  vec2 positions[6] = vec2[](
    vec2(-1.0, -1.0),
    vec2(1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, 1.0)
  );
  vec2 uvs[6] = vec2[](
    vec2(0.0, 1.0),
    vec2(1.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 0.0)
  );
  gl_Position = vec4(positions[gl_VertexID], vec2(0.0, 1.0));
  v_uv = uvs[gl_VertexID];
}`;

